(() => {
  const elementName = "spacetek-sticky-atc";

  if (customElements.get(elementName)) {
    return;
  }

  class SpacetekStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this._abortController) {
        return;
      }

      this._button = this.querySelector("[data-spacetek-sticky-atc-button]");
      this._buttonLabel = this.querySelector("[data-spacetek-sticky-atc-label]");
      this._variantTitle = this.querySelector("[data-spacetek-sticky-atc-variant]");
      this._productSection = this.closest(".shopify-section--main-product");

      const formId = this.dataset.productFormId;
      const productForm = formId ? document.getElementById(formId) : null;

      if (
        !this._button ||
        !this._buttonLabel ||
        !this._productSection ||
        !(productForm instanceof HTMLFormElement) ||
        !this._productSection.contains(productForm)
      ) {
        this.hidden = true;
        return;
      }

      this._productForm = productForm;
      this._purchaseArea = productForm.closest(".product-info__buy-buttons");

      if (!this._purchaseArea || !this._productSection.contains(this._purchaseArea)) {
        this.hidden = true;
        return;
      }

      this._abortController = new AbortController();
      this._onStickyButtonClick = this._handleStickyButtonClick.bind(this);
      this._onVariantChange = this._handleVariantChange.bind(this);
      this._onPurchaseAreaMutation = this._queueStateSync.bind(this);

      this._button.addEventListener("click", this._onStickyButtonClick, {
        signal: this._abortController.signal
      });
      this._productForm.addEventListener("variant:change", this._onVariantChange, {
        signal: this._abortController.signal
      });

      this._purchaseAreaObserver = new MutationObserver(this._onPurchaseAreaMutation);
      this._purchaseAreaObserver.observe(this._purchaseArea, {
        attributes: true,
        attributeFilter: ["aria-busy", "aria-disabled", "aria-hidden", "class", "disabled", "hidden", "style", "value"],
        characterData: true,
        childList: true,
        subtree: true
      });

      this._syncState();
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._purchaseAreaObserver?.disconnect();

      this._abortController = null;
      this._purchaseAreaObserver = null;
      this._stateSyncQueued = false;
    }

    _handleStickyButtonClick(event) {
      event.preventDefault();

      const sourceButton = this._findActivePurchaseButton();

      if (!sourceButton || this._isSourceDisabled(sourceButton) || this._isPurchaseBusy(sourceButton)) {
        this._syncState();
        return;
      }

      sourceButton.click();
      this._queueStateSync();
    }

    _handleVariantChange(event) {
      const variant = event.detail?.variant;

      if (this._variantTitle) {
        const shouldHideVariant = this.dataset.hideVariantTitle === "true" || !variant?.title;

        this._variantTitle.hidden = shouldHideVariant;

        if (!shouldHideVariant) {
          this._variantTitle.textContent = variant.title;
        }
      }

      this._queueStateSync();
    }

    _queueStateSync() {
      if (this._stateSyncQueued) {
        return;
      }

      this._stateSyncQueued = true;

      queueMicrotask(() => {
        this._stateSyncQueued = false;

        if (this.isConnected) {
          this._syncState();
        }
      });
    }

    _syncState() {
      const sourceButton = this._findActivePurchaseButton();

      if (!sourceButton) {
        this.hidden = true;
        return;
      }

      this.hidden = false;

      const sourceLabel = this._getSourceLabel(sourceButton);

      if (sourceLabel) {
        this._buttonLabel.textContent = sourceLabel;
      }

      const isBusy = this._isPurchaseBusy(sourceButton);
      const isDisabled = this._isSourceDisabled(sourceButton) || isBusy;

      this._button.disabled = isDisabled;

      if (isBusy) {
        this._button.setAttribute("aria-busy", "true");
      } else {
        this._button.removeAttribute("aria-busy");
      }

      const accessibleLabel = sourceLabel || this._buttonLabel.textContent.trim();
      const productTitle = this.dataset.productTitle;

      if (accessibleLabel && productTitle) {
        this._button.setAttribute("aria-label", `${accessibleLabel}: ${productTitle}`);
      }

      if (accessibleLabel) {
        this.setAttribute("aria-label", accessibleLabel);
      }
    }

    _findActivePurchaseButton() {
      const selectorGroups = [
        ".gpo-clone-atc-button",
        ".appikon-pre-order-button-customization",
        "buy-buttons > button[type='submit']",
        "button[type='submit'], input[type='submit']"
      ];
      const candidates = [];

      for (const selector of selectorGroups) {
        for (const match of this._purchaseArea.querySelectorAll(selector)) {
          const candidate = this._normalizeCandidate(match);

          if (candidate && !candidates.includes(candidate)) {
            candidates.push(candidate);
          }
        }
      }

      return candidates.find((candidate) => this._isPurchaseButton(candidate) && this._isRendered(candidate)) || null;
    }

    _normalizeCandidate(candidate) {
      if (candidate.matches("button, input[type='submit'], input[type='button']")) {
        return candidate;
      }

      return candidate.querySelector("button, input[type='submit'], input[type='button']") || candidate;
    }

    _isPurchaseButton(candidate) {
      return (
        candidate !== this._button &&
        typeof candidate.click === "function" &&
        !candidate.closest(".shopify-payment-button") &&
        candidate.getAttribute("name") !== "checkout"
      );
    }

    _isRendered(candidate) {
      if (candidate.hidden || candidate.getAttribute("aria-hidden") === "true") {
        return false;
      }

      const styles = window.getComputedStyle(candidate);

      return styles.display !== "none" && styles.visibility !== "hidden" && candidate.getClientRects().length > 0;
    }

    _isSourceDisabled(sourceButton) {
      return (
        sourceButton.disabled === true ||
        sourceButton.hasAttribute("disabled") ||
        sourceButton.getAttribute("aria-disabled") === "true"
      );
    }

    _isPurchaseBusy(sourceButton) {
      if (sourceButton.getAttribute("aria-busy") === "true") {
        return true;
      }

      const themeButton = this._purchaseArea.querySelector("buy-buttons > button[type='submit']");

      return themeButton?.getAttribute("aria-busy") === "true";
    }

    _getSourceLabel(sourceButton) {
      const rawLabel = sourceButton instanceof HTMLInputElement ? sourceButton.value : sourceButton.innerText;

      return rawLabel?.replace(/\s+/g, " ").trim() || "";
    }
  }

  customElements.define(elementName, SpacetekStickyAtc);
})();
